import React, { useCallback, useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import { Box, CircularProgress, Button, Pagination } from '@mui/material';
import { AnnotatorDeposit, Item, Material } from 'types/api.types';
import DepositCard from 'components/common/DepositCard';
import { getItems, getMaterials } from 'services/lookupsService';
import { getDepositsForReview } from 'services/annotatorService';
import ReviewDialog from 'components/reviews/ReviewDialog';
import { formatDateTime } from 'utils/dateFormat.utils';

const PAGE_SIZE = 10;

const Reviews: React.FC = () => {
  const [deposits, setDeposits] = useState<AnnotatorDeposit[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState<AnnotatorDeposit | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const fetchDeposits = useCallback(async (pageNum: number) => {
    setLoading(true);
    const result = await getDepositsForReview(pageNum, PAGE_SIZE);

    if (result) {
      setDeposits(result.data);
      setTotalPages(result.meta.totalPages);
    } else {
      setDeposits([]);
      setTotalPages(0);
    }

    setLoading(false);
  }, []);

  const handleReviewClick = (deposit: AnnotatorDeposit) => {
    setSelectedDeposit(deposit);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedDeposit(null);
  };

  const handleReviewSubmit = async (_depositId: number) => {
    if (deposits.length === 1 && page > 1) {
      setPage(page - 1);
      return;
    }

    await fetchDeposits(page);
  };

  useEffect(() => {
    getMaterials().then((items) => items && setMaterials(items));
    getItems().then((items) => items && setItems(items));
  }, []);

  useEffect(() => {
    fetchDeposits(page);
  }, [page, fetchDeposits]);

  if (isLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, p: 3 }}
      >
        Pending Submissions to Review
      </Typography>
      <Box sx={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {deposits.length === 0 ? (
          <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
            No pending submissions to review.
          </Typography>
        ) : (
          deposits.map((item) => (
            <Box key={item.id}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  mb: 0.5,
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography variant="caption" sx={(theme) => ({ color: theme.palette.grey[600] })}>
                    <b>{formatDateTime(new Date(item.date))}</b>
                  </Typography>
                </Box>

                <Button variant="contained" color="primary" onClick={() => handleReviewClick(item)}>
                  Review
                </Button>
              </Box>
              <DepositCard
                items={items}
                materials={materials}
                item={item}
                hideDelete={true}
                onDeleteClick={() => {}}
              />
            </Box>
          ))
        )}
      </Box>
      {totalPages > 1 && (
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => setPage(value)}
          sx={{ mt: 3, mb: 3 }}
        />
      )}
      <ReviewDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        deposit={selectedDeposit}
        items={items}
        materials={materials}
        onReviewSubmit={handleReviewSubmit}
      />
    </Box>
  );
};

export default Reviews;
